import { Component, effect, input, output } from '@angular/core';
import { IBase, IRestrictions } from 'src/app/models';
import { RestrictionsType } from 'src/app/models/enums';
import { LiteralService, UtilsService } from 'src/app/services';
import { images } from 'src/images';

@Component({
  selector: 'app-restriction-details',
  templateUrl: './restriction-details.component.html',
  styleUrl: './restriction-details.component.scss',
  standalone: false
})
export class RestrictionDetailsComponent {

  readonly restriction = input<IRestrictions>();
  readonly restrictionsType = input(RestrictionsType.BLACK_LIST);
  readonly stopOptions = input<{ value: number; label: string }[]>([]);

  readonly closeDetails = output();
  readonly save = output<{ originId: number; destinationIds: number[] }>();

  public images = images;
  public RestrictionsType = RestrictionsType;

  public destinations: IBase[] = [];
  public destinationsToAdd: IBase[] = [];
  public addDestinationOptions: IBase[] = [];
  public editMode = false;
  public expanded = false;
  public markedForRemovalIds = new Set<number>();
  public searchTerm = '';

  private readonly threeColumnsMinDestinations = 20;

  constructor(public literalService: LiteralService, public utilsService: UtilsService) {
    effect(() => {
      if (!this.restriction()) this.expanded = false;
      this.searchTerm = '';
      this.destinations = (this.restriction()?.restrictedDestinations ?? []).map(d => d.destination);
      this.destinationsToAdd = [];
      this.markedForRemovalIds = new Set<number>();
      this.editMode = false;
      this.updateAddDestinationOptions();
    });
  }

  get hasThreeColumns(): boolean {
    return this.destinations.length + this.destinationsToAdd.length > this.threeColumnsMinDestinations;
  }

  get filteredDestinations(): IBase[] {
    return this.destinations.filter(d => this.utilsService.textMatches(d.name, this.searchTerm));
  }

  get filteredDestinationsToAdd(): IBase[] {
    return this.destinationsToAdd.filter(d => this.utilsService.textMatches(d.name, this.searchTerm));
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;
  }

  removeDestination(destination: IBase): void {
    this.markedForRemovalIds.add(destination.id!);
  }

  restoreDestination(destination: IBase): void {
    this.markedForRemovalIds.delete(destination.id!);
  }

  removeStagedDestination(destination: IBase): void {
    this.destinationsToAdd = this.destinationsToAdd.filter(d => d.id !== destination.id);
    this.updateAddDestinationOptions();
  }

  hasChanges(): boolean {
    const originalIds = (this.restriction()?.restrictedDestinations ?? []).map(d => d.destination.id).sort();
    const currentIds = [...this.remainingDestinations(), ...this.destinationsToAdd].map(d => d.id).sort();
    return JSON.stringify(originalIds) !== JSON.stringify(currentIds);
  }

  cancel(): void {
    this.destinations = (this.restriction()?.restrictedDestinations ?? []).map(d => d.destination);
    this.destinationsToAdd = [];
    this.markedForRemovalIds = new Set<number>();
    this.editMode = false;
    this.updateAddDestinationOptions();
  }

  saveChanges(): void {
    const originId = this.restriction()?.originStop?.id;
    if (!originId || !this.hasChanges()) return;
    const finalDestinations = [...this.remainingDestinations(), ...this.destinationsToAdd];
    this.save.emit({ originId, destinationIds: finalDestinations.map(d => d.id!) });
    this.destinations = finalDestinations;
    this.destinationsToAdd = [];
    this.markedForRemovalIds = new Set<number>();
    this.editMode = false;
    this.updateAddDestinationOptions();
  }

  private remainingDestinations(): IBase[] {
    return this.destinations.filter(d => !this.markedForRemovalIds.has(d.id!));
  }

  close(): void {
    this.closeDetails.emit();
  }

  private updateAddDestinationOptions(): void {
    const originId = this.restriction()?.originStop?.id;
    const usedIds = new Set(this.destinations.map(d => d.id));
    this.addDestinationOptions = this.stopOptions()
      .filter(s => s.value !== originId && !usedIds.has(s.value))
      .map(s => ({ id: s.value, name: s.label }));
  }
}
